import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, addDoc, updateDoc, deleteDoc, getDocs, serverTimestamp, query, orderBy, doc, limit } from 'firebase/firestore';
import useBasePath from '../../utils/useBasePath';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { db, auth } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useMarket } from '../../contexts/MarketContext';
import { PROVINCES, REGIONS, ALICANTE_MUNICIPALITIES, teamProvince, teamRegion, provincesFor } from '../../utils/teamDatabaseGeo';
import LeadDatabase, { LEAD_STATUSES, makeNote, normalizeLog, NotesLog, AddNote } from './LeadDatabase';
import CrmDatabase, { CopyToCrmModal } from './CrmDatabase';
import { SalesFilterBar, useSalesFilters, recordOrigin } from './salesFilters';

// ─── Constants ────────────────────────────────────────────────────────────────

const PORTAL_URL = 'https://msttoursportal.co.uk';

const LEVELS   = ['Very Low', 'Low', 'Medium', 'High', 'Very High'];
const GENDERS  = ['Male', 'Female', 'Mixed'];
const TEAM_TYPE_LABELS = { school: 'School', club: 'Club', venue: 'Sports centre' };

// Records bulk-imported from the scraped Alicante dataset carry source:'scraped'.
// Everything else — clubs that registered themselves, or that staff typed in —
// counts as manual, so the two can be worked separately.
const SOURCE_OPTIONS = [
  ['manual',  'Manually added'],
  ['scraped', 'Scraped teams'],
];

function teamSource(team) {
  return team?.source === 'scraped' ? 'scraped' : 'manual';
}

const CONTACT_OPTIONS = [
  ['yes', 'Contact details present'],
  ['no',  'No contact details yet'],
];

// Worked out from the record rather than stored, so one added by hand moves out
// of the "no contact details" list the moment it is saved.
function hasContactDetails(team) {
  if (team?.coachEmail || team?.coachPhone) return true;
  return (team?.contacts || []).some((c) => c.emails?.length || c.phones?.length);
}

function SourceBadge({ team }) {
  if (teamSource(team) !== 'scraped') return null;
  return (
    <span
      title={`Imported from ${team.sourceSystem === 'ffcv' ? 'the Valencian FA register' : 'OpenStreetMap'} — not added by hand`}
      className="px-1.5 py-0.5 bg-[#FEF3C7] text-[#92400E] rounded-full text-[10px] font-semibold uppercase flex-shrink-0"
    >
      Scraped
    </span>
  );
}

// Town filter options for a province: the curated Alicante list, otherwise
// the towns actually registered in that province (free-text on the form).
function townsFor(teams, prov, region) {
  const set = new Set(
    teams
      .filter((t) => !region || teamRegion(t) === region)
      .filter((t) => !prov || teamProvince(t) === prov)
      .map((t) => t.municipality)
      .filter(Boolean)
  );
  // The curated Alicante list predates the scraped clubs, which reach a further
  // ~35 towns, so merge rather than replace or those towns can never be picked.
  if (prov === 'Alicante' || (!prov && region !== 'Canary Islands')) {
    ALICANTE_MUNICIPALITIES.forEach((m) => set.add(m));
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

const INVITE_MSG = {
  en: (url) =>
    `Hi, MST Sports Tours is building a local team database to help us find fixtures for visiting sports teams. If you'd like to register your team(s) and receive fixture opportunities, please complete this quick form: ${url}\n\nThe more details you add, the more likely we are to match you with a suitable opponent. For any questions, email team@mstsportstours.com`,
  es: (url) =>
    `Hola, MST Sports Tours está creando una base de datos de equipos locales para ayudarnos a encontrar partidos para equipos visitantes. Si deseas registrar tu(s) equipo(s) y recibir oportunidades de partido, completa este formulario rápido: ${url}\n\nCuantos más detalles añadas, más posibilidades tendremos de encontrarte un rival adecuado. Para cualquier consulta, escribe a team@mstsportstours.com`,
};

const JERSEY_PATH = 'M 8,8 L 22,18 Q 36,8 50,12 Q 64,8 78,18 L 92,8 L 100,30 L 82,42 L 78,110 L 22,110 L 18,42 L 0,30 Z';
const KIT_COLOURS = [
  { name: 'White', hex: '#FFFFFF', border: true }, { name: 'Black', hex: '#111827' },
  { name: 'Grey', hex: '#6B7280' }, { name: 'Red', hex: '#DC2626' },
  { name: 'Blue', hex: '#1D4ED8' }, { name: 'Yellow', hex: '#FBBF24' },
  { name: 'Green', hex: '#16A34A' }, { name: 'Orange', hex: '#EA580C' },
  { name: 'Navy', hex: '#1e3a5f' }, { name: 'Maroon', hex: '#7f1d1d' },
  { name: 'Sky Blue', hex: '#38bdf8' }, { name: 'Purple', hex: '#7c3aed' },
  { name: 'Pink', hex: '#ec4899' },
];
const COLOUR_HEX = Object.fromEntries(KIT_COLOURS.map((c) => [c.name.toLowerCase(), c.hex]));

function colourToHex(n) {
  if (!n) return null;
  if (n.startsWith('#')) return n;
  return COLOUR_HEX[n.toLowerCase()] || null;
}

// ─── Kit model (identical to TeamInfoForm) ────────────────────────────────────

function KitModel({ jerseyColour, jerseyColour2, jerseyPattern, shortsColour, socksColour, uid, size = 'sm' }) {
  const c1 = colourToHex(jerseyColour)  || '#E5E7EB';
  const c2 = colourToHex(jerseyColour2) || '#FFFFFF';
  const sc = colourToHex(shortsColour)  || '#E5E7EB';
  const sk = colourToHex(socksColour)   || '#E5E7EB';
  const pat = jerseyPattern || 'plain';
  const w = size === 'sm' ? 44 : 88, h = size === 'sm' ? 96 : 193;
  return (
    <svg viewBox="0 0 100 220" width={w} height={h} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <clipPath id={`jclip-${uid}`}><path d={JERSEY_PATH} /></clipPath>
        {pat === 'stripes' && (
          <pattern id={`jpat-${uid}`} x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
            <rect x="0" y="0" width="5" height="10" fill={c1} /><rect x="5" y="0" width="5" height="10" fill={c2} />
          </pattern>
        )}
        {pat === 'hoops' && (
          <pattern id={`jpat-${uid}`} x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
            <rect x="0" y="0" width="10" height="5" fill={c1} /><rect x="0" y="5" width="10" height="5" fill={c2} />
          </pattern>
        )}
      </defs>
      {pat === 'plain' && <path d={JERSEY_PATH} fill={c1} />}
      {(pat === 'stripes' || pat === 'hoops') && (
        <g clipPath={`url(#jclip-${uid})`}><rect x="0" y="0" width="100" height="115" fill={`url(#jpat-${uid})`} /></g>
      )}
      {pat === 'halves' && (
        <g clipPath={`url(#jclip-${uid})`}>
          <rect x="0" y="0" width="50" height="115" fill={c1} />
          <rect x="50" y="0" width="50" height="115" fill={c2} />
        </g>
      )}
      <path d={JERSEY_PATH} fill="none" stroke="#9CA3AF" strokeWidth="1.5" />
      <rect x="24" y="116" width="52" height="48" rx="4" fill={sc} stroke="#9CA3AF" strokeWidth="1.5" />
      <rect x="26" y="170" width="20" height="46" rx="3" fill={sk} stroke="#9CA3AF" strokeWidth="1.5" />
      <rect x="54" y="170" width="20" height="46" rx="3" fill={sk} stroke="#9CA3AF" strokeWidth="1.5" />
    </svg>
  );
}

// ─── Kit / squad editing ──────────────────────────────────────────────────────

const JERSEY_PATTERNS = [
  { id: 'plain',   label: 'Plain' },
  { id: 'stripes', label: 'Stripes' },
  { id: 'hoops',   label: 'Hoops' },
  { id: 'halves',  label: 'Half & half' },
];

function blankSquad() {
  return {
    label: '', yearsOfBirth: [], gender: '', level: '',
    homeJerseyColour: '', homeJerseyColour2: '', homeJerseyPattern: 'plain',
    homeShortsColour: '', homeSocksColour: '',
    onlyOneKit: true,
    awayJerseyColour: '', awayJerseyColour2: '', awayJerseyPattern: 'plain',
    awayShortsColour: '', awaySocksColour: '',
  };
}

function ColourPicker({ label, value, onChange }) {
  return (
    <div>
      <p className="text-[11px] font-medium text-[#6B7280] mb-1">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {KIT_COLOURS.map((c) => (
          <button
            key={c.name} type="button" title={c.name} aria-label={c.name}
            aria-pressed={value === c.name}
            onClick={() => onChange(value === c.name ? '' : c.name)}
            style={{ backgroundColor: c.hex }}
            className={`w-7 h-7 rounded-full transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-[#1A4A8A] ${
              value === c.name ? 'ring-2 ring-[#1A4A8A] ring-offset-1 scale-110' : ''
            } ${c.border ? 'border border-[#D1D5DB]' : ''}`}
          />
        ))}
      </div>
    </div>
  );
}

function PatternPicker({ value, onChange }) {
  return (
    <div>
      <p className="text-[11px] font-medium text-[#6B7280] mb-1">Jersey pattern</p>
      <div className="flex flex-wrap gap-1.5">
        {JERSEY_PATTERNS.map((pt) => (
          <button
            key={pt.id} type="button" onClick={() => onChange(pt.id)} aria-pressed={value === pt.id}
            className={`px-2.5 py-1 rounded-[8px] border text-[11px] font-medium transition ${
              (value || 'plain') === pt.id
                ? 'border-[#1A4A8A] bg-[#EFF6FF] text-[#1A4A8A]'
                : 'border-[#E5E7EB] text-[#6B7280] hover:border-[#9CA3AF]'
            }`}
          >
            {pt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// One squad's kit — used twice per squad, for the home and the away strip.
function KitFields({ prefix, squad, onField }) {
  const f = (n) => `${prefix}${n}`;
  const pattern = squad[f('JerseyPattern')] || 'plain';
  return (
    <div className="space-y-2.5">
      <ColourPicker label="Jersey" value={squad[f('JerseyColour')]} onChange={(v) => onField(f('JerseyColour'), v)} />
      <PatternPicker value={pattern} onChange={(v) => onField(f('JerseyPattern'), v)} />
      {pattern !== 'plain' && (
        <ColourPicker label="Second jersey colour" value={squad[f('JerseyColour2')]} onChange={(v) => onField(f('JerseyColour2'), v)} />
      )}
      <ColourPicker label="Shorts" value={squad[f('ShortsColour')]} onChange={(v) => onField(f('ShortsColour'), v)} />
      <ColourPicker label="Socks"  value={squad[f('SocksColour')]}  onChange={(v) => onField(f('SocksColour'), v)} />
    </div>
  );
}

// ─── Shared UI ────────────────────────────────────────────────────────────────

function Label({ children, required }) {
  return (
    <label className="block text-[13px] font-medium text-[#374151] mb-1">
      {children}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
  );
}

function Input({ value, onChange, placeholder, type = 'text', ...rest }) {
  return (
    <input type={type} value={value} onChange={onChange} placeholder={placeholder}
      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A]"
      {...rest}
    />
  );
}

function Sel({ value, onChange, children }) {
  return (
    <select value={value} onChange={onChange}
      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A] bg-white"
    >
      {children}
    </select>
  );
}

function Modal({ onClose, title, children, wide }) {
  return (
    <div className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/40 overflow-y-auto py-8 px-4">
      <div className={`bg-white rounded-[14px] shadow-xl w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} my-auto`}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E7EB]">
          <p className="text-[15px] font-bold text-[#111827]">{title}</p>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#374151] transition text-xl leading-none">×</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

// ─── Level badge ──────────────────────────────────────────────────────────────

const LEVEL_COLOURS = {
  'Very Low': 'bg-gray-100 text-gray-600',
  'Low':      'bg-blue-100 text-blue-700',
  'Medium':   'bg-yellow-100 text-yellow-700',
  'High':     'bg-orange-100 text-orange-700',
  'Very High':'bg-red-100 text-red-700',
};

function LevelBadge({ level }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${LEVEL_COLOURS[level] || 'bg-gray-100 text-gray-600'}`}>
      {level}
    </span>
  );
}

// ─── Sales status (UK-tour lead pipeline on Spanish clubs) ────────────────────

const SALES_STATUS_COLOURS = {
  'New':            'bg-blue-100 text-blue-700',
  'Contacted':      'bg-yellow-100 text-yellow-700',
  'Follow-up':      'bg-orange-100 text-orange-700',
  'Quoted':         'bg-purple-100 text-purple-700',
  'Won':            'bg-green-100 text-green-700',
  'Not interested': 'bg-gray-100 text-gray-500',
};

function SalesBadge({ status }) {
  if (!status) return null;
  return (
    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${SALES_STATUS_COLOURS[status] || 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  );
}

function getUserRoles(u) {
  if (Array.isArray(u.roles)) return u.roles;
  if (u.role) return [u.role];
  return [];
}

// ─── Status badge for responses ───────────────────────────────────────────────

function StatusBadge({ status }) {
  const map = {
    pending: 'bg-gray-100 text-gray-500',
    yes:     'bg-green-100 text-green-700',
    no:      'bg-red-100 text-red-700',
    suggest: 'bg-blue-100 text-blue-700',
  };
  const labels = { pending: 'Pending', yes: 'Confirmed', no: 'Declined', suggest: 'Alternative suggested' };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${map[status] || map.pending}`}>
      {labels[status] || status}
    </span>
  );
}

// ─── YearsOfBirth multi-select ────────────────────────────────────────────────

function YobInput({ values, onChange }) {
  const [inputVal, setInputVal] = useState('');
  function add() {
    const y = inputVal.trim();
    const n = Number(y);
    if (!y || isNaN(n) || n < 1980 || n > 2020) return;
    if (values.includes(y)) { setInputVal(''); return; }
    onChange([...values, y].sort((a, b) => a - b));
    setInputVal('');
  }
  return (
    <div>
      <div className="flex gap-2 mb-2">
        <input
          type="number" min="1980" max="2020" value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())}
          placeholder="e.g. 2010"
          className="flex-1 border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A]"
        />
        <button type="button" onClick={add} className="px-3 py-2 bg-[#1A4A8A] text-white text-[13px] font-semibold rounded-[8px] hover:bg-[#0D2240] transition">
          Add
        </button>
      </div>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {values.map((y) => (
            <span key={y} className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#EFF6FF] border border-[#93C5FD] rounded-full text-[13px] font-semibold text-[#1A4A8A]">
              {y}
              <button type="button" onClick={() => onChange(values.filter((v) => v !== y))} className="text-[#93C5FD] hover:text-[#1A4A8A] leading-none">×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Date / Time selects (avoids ugly native mobile date pickers) ─────────────

const MONTHS_SHORT = [
  { val: '01', label: 'Jan' }, { val: '02', label: 'Feb' }, { val: '03', label: 'Mar' },
  { val: '04', label: 'Apr' }, { val: '05', label: 'May' }, { val: '06', label: 'Jun' },
  { val: '07', label: 'Jul' }, { val: '08', label: 'Aug' }, { val: '09', label: 'Sep' },
  { val: '10', label: 'Oct' }, { val: '11', label: 'Nov' }, { val: '12', label: 'Dec' },
];

const selCls = "flex-1 border border-[#E5E7EB] rounded-[8px] px-2 py-2.5 text-[14px] text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A] bg-white";

function DateSelect({ value, onChange }) {
  const [y, setY] = useState(value ? value.slice(0, 4) : '');
  const [m, setM] = useState(value ? value.slice(5, 7) : '');
  const [d, setD] = useState(value ? value.slice(8, 10) : '');

  function emitIfComplete(newY, newM, newD) {
    if (!newY || !newM || !newD) { onChange(''); return; }
    const maxDay = new Date(parseInt(newY), parseInt(newM), 0).getDate();
    const cd = String(Math.min(parseInt(newD), maxDay)).padStart(2, '0');
    onChange(`${newY}-${newM}-${cd}`);
  }

  function handleY(val) { setY(val); emitIfComplete(val, m, d); }
  function handleM(val) { setM(val); emitIfComplete(y, val, d); }
  function handleD(val) { setD(val); emitIfComplete(y, m, val); }

  const thisYear = new Date().getFullYear();
  const years    = Array.from({ length: 4 }, (_, i) => String(thisYear + i));
  const maxDay   = (y && m) ? new Date(parseInt(y), parseInt(m), 0).getDate() : 31;
  const days     = Array.from({ length: maxDay }, (_, i) => String(i + 1).padStart(2, '0'));

  return (
    <div className="flex gap-2">
      <select value={d} onChange={(e) => handleD(e.target.value)} className={selCls}>
        <option value="">Day</option>
        {days.map((dd) => <option key={dd} value={dd}>{parseInt(dd)}</option>)}
      </select>
      <select value={m} onChange={(e) => handleM(e.target.value)} className={selCls}>
        <option value="">Month</option>
        {MONTHS_SHORT.map((mo) => <option key={mo.val} value={mo.val}>{mo.label}</option>)}
      </select>
      <select value={y} onChange={(e) => handleY(e.target.value)} className={selCls}>
        <option value="">Year</option>
        {years.map((yr) => <option key={yr} value={yr}>{yr}</option>)}
      </select>
    </div>
  );
}

function TimeSelect({ value, onChange }) {
  const [h,   setH]   = useState(value ? value.slice(0, 2) : '');
  const [min, setMin] = useState(value ? value.slice(3, 5) : '');

  function handleH(val)   { setH(val);   if (val && min) onChange(`${val}:${min}`); else onChange(''); }
  function handleMin(val) { setMin(val); if (h && val)   onChange(`${h}:${val}`);   else onChange(''); }

  const hours   = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
  const minutes = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

  return (
    <div className="flex gap-2">
      <select value={h} onChange={(e) => handleH(e.target.value)} className={selCls}>
        <option value="">Hour</option>
        {hours.map((hr) => <option key={hr} value={hr}>{hr}</option>)}
      </select>
      <select value={min} onChange={(e) => handleMin(e.target.value)} className={selCls}>
        <option value="">Min</option>
        {minutes.map((mn) => <option key={mn} value={mn}>{mn}</option>)}
      </select>
    </div>
  );
}

// ─── Team detail modal ────────────────────────────────────────────────────────

// Full record editor. A scraped club arrives with squads but no kit and often a
// rough name, so staff need everything the registration form captures. Existing
// squad fields are spread through untouched, keeping the scraped competition
// detail (category, ground, match day) that this form does not show.
function TeamEditor({ team, onCancel, onSave }) {
  const [clubName, setClubName]       = useState(team.clubName || '');
  const [teamType, setTeamType]       = useState(team.teamType || '');
  const [address, setAddress]         = useState(team.address || '');
  const [municipality, setMunicipality] = useState(team.municipality || '');
  const [province, setProvince]       = useState(teamProvince(team));
  const [squads, setSquads]           = useState(() => (team.squads || []).map((sq) => ({ ...sq })));
  const [error, setError]             = useState('');

  function updateSquad(i, field, value) {
    setSquads((prev) => prev.map((sq, n) => (n === i ? { ...sq, [field]: value } : sq)));
  }

  function save() {
    if (!clubName.trim()) { setError('A club or school name is required.'); return; }
    onSave({
      clubName: clubName.trim(),
      teamType,
      address: address.trim(),
      municipality: municipality.trim(),
      province,
      region: (province || 'Alicante').toLowerCase(),
      squads: squads.map((sq) => ({
        ...sq,
        label: (sq.label || '').trim(),
        yearsOfBirth: sq.yearsOfBirth || [],
      })),
    });
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label required>Club / school name</Label>
          <Input value={clubName} onChange={(e) => setClubName(e.target.value)} />
        </div>
        <div>
          <Label>Type</Label>
          <Sel value={teamType} onChange={(e) => setTeamType(e.target.value)}>
            <option value="">Not set</option>
            {Object.entries(TEAM_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </Sel>
        </div>
        <div>
          <Label>Province</Label>
          <Sel value={province} onChange={(e) => setProvince(e.target.value)}>
            {PROVINCES.map((pr) => <option key={pr}>{pr}</option>)}
          </Sel>
        </div>
        <div>
          <Label>Town</Label>
          <Input value={municipality} onChange={(e) => setMunicipality(e.target.value)} />
        </div>
        <div>
          <Label>Address</Label>
          <Input value={address} onChange={(e) => setAddress(e.target.value)} />
        </div>
      </div>

      <div className="flex items-center justify-between pt-1">
        <p className="text-[13px] font-semibold text-[#374151]">{squads.length} team(s)</p>
        <button type="button" onClick={() => setSquads((prev) => [...prev, blankSquad()])}
          className="text-[12px] text-[#1A4A8A] hover:underline font-semibold">
          + Add a team
        </button>
      </div>

      <div className="space-y-4">
        {squads.map((sq, i) => (
          <div key={i} className="border border-[#E5E7EB] rounded-[10px] p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <Label>Team name</Label>
                <Input value={sq.label || ''} onChange={(e) => updateSquad(i, 'label', e.target.value)}
                  placeholder={`Team ${i + 1}`} />
                {sq.category && <p className="text-[11px] text-[#9CA3AF] mt-1">{sq.category}</p>}
              </div>
              <button type="button" onClick={() => setSquads((prev) => prev.filter((_, n) => n !== i))}
                className="text-[11px] text-[#DC2626] hover:underline font-semibold mt-6 flex-shrink-0">
                Remove
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Gender</Label>
                <Sel value={sq.gender || ''} onChange={(e) => updateSquad(i, 'gender', e.target.value)}>
                  <option value="">Not set</option>
                  {GENDERS.map((g) => <option key={g}>{g}</option>)}
                </Sel>
              </div>
              <div>
                <Label>Standard</Label>
                <Sel value={sq.level || ''} onChange={(e) => updateSquad(i, 'level', e.target.value)}>
                  <option value="">Not set</option>
                  {LEVELS.map((l) => <option key={l}>{l}</option>)}
                </Sel>
              </div>
            </div>

            <div>
              <Label>Year(s) of birth</Label>
              <YobInput values={sq.yearsOfBirth || []} onChange={(v) => updateSquad(i, 'yearsOfBirth', v)} />
            </div>

            <div className="border-t border-[#F3F4F6] pt-3">
              <div className="flex items-start gap-4">
                <div className="flex-1 space-y-3">
                  <p className="text-[12px] font-semibold text-[#374151]">Home kit</p>
                  <KitFields prefix="home" squad={sq} onField={(f, v) => updateSquad(i, f, v)} />
                </div>
                {sq.homeJerseyColour && (
                  <div className="text-center flex-shrink-0">
                    <p className="text-[11px] text-[#9CA3AF] mb-1">Preview</p>
                    <KitModel uid={`edit-home-${team.id}-${i}`}
                      jerseyColour={sq.homeJerseyColour} jerseyColour2={sq.homeJerseyColour2}
                      jerseyPattern={sq.homeJerseyPattern} shortsColour={sq.homeShortsColour} socksColour={sq.homeSocksColour}
                    />
                  </div>
                )}
              </div>

              <label className="flex items-center gap-2 text-[13px] text-[#374151] mt-3 cursor-pointer">
                <input type="checkbox" checked={sq.onlyOneKit !== false}
                  onChange={(e) => updateSquad(i, 'onlyOneKit', e.target.checked)} className="rounded" />
                This team only has one kit
              </label>

              {sq.onlyOneKit === false && (
                <div className="flex items-start gap-4 mt-3">
                  <div className="flex-1 space-y-3">
                    <p className="text-[12px] font-semibold text-[#374151]">Away kit</p>
                    <KitFields prefix="away" squad={sq} onField={(f, v) => updateSquad(i, f, v)} />
                  </div>
                  {sq.awayJerseyColour && (
                    <div className="text-center flex-shrink-0">
                      <p className="text-[11px] text-[#9CA3AF] mb-1">Preview</p>
                      <KitModel uid={`edit-away-${team.id}-${i}`}
                        jerseyColour={sq.awayJerseyColour} jerseyColour2={sq.awayJerseyColour2}
                        jerseyPattern={sq.awayJerseyPattern} shortsColour={sq.awayShortsColour} socksColour={sq.awaySocksColour}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {error && <p className="text-[13px] text-[#DC2626]">{error}</p>}

      <div className="flex gap-2 pt-1">
        <button onClick={save}
          className="px-4 py-2 bg-[#1A4A8A] text-white text-[13px] font-semibold rounded-[8px] hover:bg-[#0D2240] transition">
          Save changes
        </button>
        <button onClick={onCancel}
          className="px-4 py-2 bg-white border border-[#E5E7EB] text-[13px] font-semibold text-[#374151] rounded-[8px] hover:bg-[#F9FAFB] transition">
          Cancel
        </button>
      </div>
    </div>
  );
}

function TeamDetailModal({ team, staff, currentUser, canManage, onClose, onPatch, onDelete }) {
  const salesEntries = normalizeLog(team.salesNoteLog, team.salesNotes);

  // Most of the scraped schools and venues arrive with no contact details at
  // all, so staff need to be able to put them in by hand.
  const [editingContact, setEditingContact] = useState(false);
  const [editingTeam, setEditingTeam] = useState(false);
  const [draft, setDraft] = useState({ name: '', email: '', phone: '', website: '' });

  function startEditContact() {
    const primary = (team.contacts || [])[0];
    setDraft({
      name:  team.coachName  || primary?.name || '',
      email: team.coachEmail || (primary?.emails || [])[0] || '',
      phone: team.coachPhone || (primary?.phones || [])[0] || '',
      website: team.website || '',
    });
    setEditingContact(true);
  }

  function saveTeam(patch) {
    onPatch(withSalesNote('Team details updated', true, patch));
    setEditingTeam(false);
  }

  // The form edits the primary contact. Any further numbers or addresses the
  // scrape found for that person are kept behind the edited one, and any other
  // people on the record are left alone — opening and saving this form must
  // never quietly drop a contact detail.
  function saveContact() {
    const had = hasContactDetails(team);
    const email = draft.email.trim().toLowerCase();
    const phone = draft.phone.trim();
    const [first, ...rest] = team.contacts || [];
    const emails = [email, ...((first?.emails) || []).filter((e) => e !== email)].filter(Boolean);
    const phones = [phone, ...((first?.phones) || []).filter((ph) => ph !== phone)].filter(Boolean);
    const primary = {
      name: draft.name.trim(),
      title: first?.title || '',
      role: first?.role || 'Main contact',
      emails,
      phones,
    };
    const contacts = (primary.name || emails.length || phones.length) ? [primary, ...rest] : rest;
    onPatch(withSalesNote(had ? 'Contact details updated' : 'Contact details added', true, {
      coachName:  primary.name,
      coachEmail: emails[0] || '',
      coachPhone: phones[0] || '',
      website:    draft.website.trim(),
      contacts,
    }));
    setEditingContact(false);
  }

  // Every sales mutation folds the existing log forward and clears the legacy
  // salesNotes string, so the log becomes the single source of truth.
  function withSalesNote(text, system, extra = {}) {
    const existing = normalizeLog(team.salesNoteLog, team.salesNotes);
    return { ...extra, salesNoteLog: [makeNote(text, currentUser, system), ...existing], salesNotes: '' };
  }

  function addSalesNote(text) { onPatch(withSalesNote(text, false)); }
  function changeSalesStatus(s) {
    if (s === (team.salesStatus || '')) return;
    onPatch(withSalesNote(s ? `Status changed to ${s}` : 'Status cleared', true, { salesStatus: s }));
  }
  function changeSalesAssignee(uid) {
    const a = staff.find((x) => x.id === uid);
    const name = a ? (a.name || a.email || '') : '';
    onPatch(withSalesNote(uid ? `Assigned to ${name}` : 'Unassigned', true, { salesAssignedTo: uid, salesAssignedToName: name }));
  }
  function markSalesContacted() {
    const s = (team.salesStatus === 'New' || !team.salesStatus) ? 'Contacted' : team.salesStatus;
    onPatch(withSalesNote('Marked as contacted', true, { salesLastContactedAt: serverTimestamp(), salesStatus: s }));
  }

  const lastContacted = team.salesLastContactedAt
    ? (team.salesLastContactedAt.toDate ? team.salesLastContactedAt.toDate() : new Date(team.salesLastContactedAt))
    : null;

  return (
    <Modal onClose={onClose} title={team.clubName} wide>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4 text-[13px]">
          <div>
            <p className="text-[#9CA3AF] text-[11px] font-medium uppercase tracking-wide mb-0.5">Address</p>
            <p className="text-[#111827]">{team.address}</p>
            <p className="text-[#6B7280]">{team.municipality} · {teamProvince(team)}</p>
            <p className="text-[#6B7280] mt-1 flex items-center gap-1.5">
              {team.teamType && (
                <span className="px-1.5 py-0.5 bg-[#EFF6FF] text-[#1A4A8A] rounded-full text-[10px] font-semibold uppercase">
                  {TEAM_TYPE_LABELS[team.teamType] || team.teamType}
                </span>
              )}
              <SourceBadge team={team} />
            </p>
            {team.sourceUrl && (
              <a href={team.sourceUrl} target="_blank" rel="noreferrer"
                className="text-[11px] text-[#1A4A8A] hover:underline mt-1 inline-block">
                View source record ↗
              </a>
            )}
          </div>
          <div>
            <div className="flex items-center justify-between mb-0.5">
              <p className="text-[#9CA3AF] text-[11px] font-medium uppercase tracking-wide">
                {team.contacts?.length > 1 ? `Contacts (${team.contacts.length})` : 'Contact'}
              </p>
              {!editingContact && canManage && (
                <button onClick={startEditContact} className="text-[11px] text-[#1A4A8A] hover:underline font-semibold">
                  {hasContactDetails(team) ? 'Edit' : 'Add contact details'}
                </button>
              )}
            </div>

            {editingContact ? (
              <div className="space-y-2">
                <Input value={draft.name}  onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}  placeholder="Contact name" />
                <Input value={draft.email} onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))} placeholder="Email address" type="email" />
                <Input value={draft.phone} onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))} placeholder="Phone number" />
                <Input value={draft.website} onChange={(e) => setDraft((d) => ({ ...d, website: e.target.value }))} placeholder="Website" />
                <div className="flex gap-2 pt-1">
                  <button onClick={saveContact}
                    className="px-3 py-1.5 bg-[#1A4A8A] text-white text-[12px] font-semibold rounded-[8px] hover:bg-[#0D2240] transition">
                    Save
                  </button>
                  <button onClick={() => setEditingContact(false)}
                    className="px-3 py-1.5 bg-white border border-[#E5E7EB] text-[12px] font-semibold text-[#374151] rounded-[8px] hover:bg-[#F9FAFB] transition">
                    Cancel
                  </button>
                </div>
              </div>
            ) : !hasContactDetails(team) ? (
              <p className="text-[13px] text-[#9CA3AF] italic">
                No contact details yet — add them to make this record contactable.
              </p>
            ) : team.contacts?.length ? (
              <div className="space-y-2">
                {team.contacts.map((c, i) => (
                  <div key={i} className={i > 0 ? 'pt-2 border-t border-[#F3F4F6]' : ''}>
                    {c.name && <p className="text-[#111827]">{[c.title, c.name].filter(Boolean).join(' ')}</p>}
                    {c.role && <p className="text-[11px] text-[#9CA3AF]">{c.role}</p>}
                    {(c.emails || []).map((e) => (
                      <a key={e} href={`mailto:${e}`} className="block text-[#1A4A8A] hover:underline break-all">{e}</a>
                    ))}
                    {(c.phones || []).map((ph) => (
                      <a key={ph} href={`tel:+34${ph.replace(/\s/g, '')}`} className="block text-[#6B7280] hover:underline">{ph}</a>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <>
                <p className="text-[#111827]">{team.coachName}</p>
                <p className="text-[#6B7280]">{team.coachEmail}</p>
                {team.coachPhone && <p className="text-[#6B7280]">{team.coachPhone}</p>}
              </>
            )}
            {team.website && (
              <a href={team.website.startsWith('http') ? team.website : `https://${team.website}`}
                target="_blank" rel="noreferrer" className="block text-[12px] text-[#1A4A8A] hover:underline mt-1 break-all">
                {team.website}
              </a>
            )}
            <p className={`mt-1 text-[12px] font-medium ${team.marketingConsent ? 'text-[#065F46]' : 'text-[#9CA3AF]'}`}>
              {team.marketingConsent ? '✓ Opted in to England tours marketing' : 'No marketing consent'}
            </p>
          </div>
        </div>

        {/* UK-tour sales pipeline — a sales function, so staff/admin only */}
        {canManage && (
        <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-[10px] p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[12px] font-semibold text-[#374151] uppercase tracking-wide">UK tour sales</p>
            {lastContacted && (
              <p className="text-[11px] text-[#9CA3AF]">
                Last contacted: {lastContacted.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <Label>Status</Label>
              <Sel value={team.salesStatus || ''} onChange={(e) => changeSalesStatus(e.target.value)}>
                <option value="">Not started</option>
                {LEAD_STATUSES.map((s) => <option key={s}>{s}</option>)}
              </Sel>
            </div>
            <div>
              <Label>Assigned to</Label>
              <Sel value={team.salesAssignedTo || ''} onChange={(e) => changeSalesAssignee(e.target.value)}>
                <option value="">Unassigned</option>
                {staff.map((s) => <option key={s.id} value={s.id}>{s.name || s.email}</option>)}
              </Sel>
            </div>
          </div>
          <div className="flex items-center justify-between mb-2">
            <Label>Sales notes &amp; activity</Label>
            <button onClick={markSalesContacted} className="text-[12px] text-[#065F46] hover:underline font-semibold">Mark contacted today</button>
          </div>
          <div className="mb-3"><AddNote onAdd={addSalesNote} /></div>
          <NotesLog entries={salesEntries} />
        </div>
        )}

        <hr className="border-[#E5E7EB]" />
        {editingTeam ? (
          <TeamEditor team={team} onCancel={() => setEditingTeam(false)} onSave={saveTeam} />
        ) : (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-[13px] font-semibold text-[#374151]">{team.squads?.length || 0} team(s)</p>
            {canManage && (
              <button onClick={() => setEditingTeam(true)} className="text-[12px] text-[#1A4A8A] hover:underline font-semibold">
                Edit club details, teams &amp; kit
              </button>
            )}
          </div>
          {!team.squads?.length && (
            <p className="text-[13px] text-[#9CA3AF] italic mb-3">
              No teams recorded yet — add them to make this club matchable on age, standard and kit.
            </p>
          )}
          <div className="space-y-4">
            {(team.squads || []).map((sq, i) => (
              <div key={i} className="border border-[#E5E7EB] rounded-[10px] p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[13px] font-semibold text-[#111827]">{sq.label || `Squad ${i + 1}`}</p>
                  <div className="flex gap-2 items-center">
                    <span className="text-[12px] text-[#6B7280]">{sq.gender}</span>
                    <LevelBadge level={sq.level} />
                  </div>
                </div>
                <p className="text-[12px] text-[#6B7280] mb-1">
                  Year(s) of birth: <span className="font-semibold text-[#374151]">{(sq.yearsOfBirth || []).join(', ') || '—'}</span>
                  {sq.ageBand && <span className="ml-2 text-[#9CA3AF]">({sq.ageBand})</span>}
                </p>
                {(sq.category || sq.sport) && (
                  <p className="text-[12px] text-[#6B7280] mb-1">
                    {[sq.sport, sq.category].filter(Boolean).join(' · ')}
                  </p>
                )}
                {sq.ground && (
                  <p className="text-[12px] text-[#6B7280] mb-1">
                    {sq.ground}{sq.surface ? ` · ${sq.surface}` : ''}
                    {sq.matchDay ? ` · plays ${sq.matchDay}${sq.kickoff ? ` ${sq.kickoff}` : ''}` : ''}
                  </p>
                )}
                <div className="flex gap-6 mt-3">
                  {sq.homeJerseyColour && (
                  <div className="text-center">
                    <p className="text-[11px] text-[#9CA3AF] mb-1">Home</p>
                    <KitModel uid={`detail-home-${team.id}-${i}`}
                      jerseyColour={sq.homeJerseyColour} jerseyColour2={sq.homeJerseyColour2}
                      jerseyPattern={sq.homeJerseyPattern} shortsColour={sq.homeShortsColour} socksColour={sq.homeSocksColour}
                    />
                  </div>
                  )}
                  {!sq.onlyOneKit && sq.awayJerseyColour && (
                    <div className="text-center">
                      <p className="text-[11px] text-[#9CA3AF] mb-1">Away</p>
                      <KitModel uid={`detail-away-${team.id}-${i}`}
                        jerseyColour={sq.awayJerseyColour} jerseyColour2={sq.awayJerseyColour2}
                        jerseyPattern={sq.awayJerseyPattern} shortsColour={sq.awayShortsColour} socksColour={sq.awaySocksColour}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        )}

        {/* Footer — deleting a club is staff/admin only and removes the record
            from the fixture pool as well as the sales pipeline. */}
        {canManage && (
          <div className="flex items-center justify-between pt-3 border-t border-[#E5E7EB]">
            <button onClick={onDelete} className="text-[13px] text-[#DC2626] hover:underline font-medium">
              Delete club
            </button>
            <button onClick={onClose} className="px-4 py-2 bg-[#1A4A8A] text-white text-[13px] font-semibold rounded-[8px] hover:bg-[#0D2240] transition">
              Done
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─── New fixture modal ────────────────────────────────────────────────────────

function NewFixtureModal({ teams, onClose, onSent }) {
  const [date, setDate]       = useState('');
  const [time, setTime]       = useState('');
  const [venue, setVenue]     = useState('');
  const [yobs, setYobs]       = useState([]);
  const [level, setLevel]     = useState('');
  const [gender, setGender]   = useState('');
  const [lang, setLang]       = useState('en');

  // Team selection filters
  const [filterYob, setFilterYob]         = useState('');
  const [filterProvince, setFilterProvince] = useState('');
  const [filterMuni, setFilterMuni]       = useState('');
  const [filterLevel, setFilterLevel]     = useState('');
  const [filterGender, setFilterGender]   = useState('');
  const [filterSource, setFilterSource]   = useState('');

  const [selectedIds, setSelectedIds]     = useState([]);
  const [step, setStep]                   = useState(1); // 1 = fixture details, 2 = select teams, 3 = confirm
  const [sending, setSending]             = useState(false);
  const [error, setError]                 = useState('');

  const filteredTeams = teams.filter((team) => {
    if (filterProvince && teamProvince(team) !== filterProvince) return false;
    if (filterMuni && team.municipality !== filterMuni) return false;
    if (filterSource && teamSource(team) !== filterSource) return false;
    if (!team.squads?.length) return false;
    return team.squads.some((sq) => {
      if (filterYob && !sq.yearsOfBirth?.includes(filterYob)) return false;
      if (filterLevel && sq.level !== filterLevel) return false;
      if (filterGender && sq.gender !== filterGender) return false;
      return true;
    });
  });

  function toggleTeam(id) {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }

  function selectAll() { setSelectedIds(filteredTeams.map((t) => t.id)); }
  function clearAll()  { setSelectedIds([]); }

  async function handleSend() {
    setError('');
    setSending(true);
    try {
      // 1. Save the fixture request
      const fixtureRef = await addDoc(collection(db, 'fixtureRequests'), {
        date, time, venue,
        yearsOfBirth: yobs,
        level, gender, language: lang,
        region: (filterProvince || 'Spain').toLowerCase(),
        createdBy: auth.currentUser?.email || '',
        createdAt: serverTimestamp(),
      });

      // 2. Write trigger doc — server-side Firestore trigger sends the emails
      const fixture = { date, time, venue, yearsOfBirth: yobs, level, gender };
      const triggerRef = await addDoc(collection(db, 'fixtureEmailRequests'), {
        fixtureId: fixtureRef.id,
        teamIds: selectedIds,
        fixture,
        language: lang,
        requestedAt: serverTimestamp(),
        status: 'pending',
      });

      // 3. Wait for the trigger to complete (poll the trigger doc)
      await new Promise((resolve, reject) => {
        const unsub = onSnapshot(doc(db, 'fixtureEmailRequests', triggerRef.id), (snap) => {
          const d = snap.data();
          if (!d) return;
          if (d.status === 'sent') { unsub(); resolve(d.sent || 0); }
          if (d.status === 'failed') { unsub(); reject(new Error(d.error || 'Send failed')); }
        });
        setTimeout(() => { unsub(); reject(new Error('Timed out waiting for emails to send')); }, 30000);
      }).then((sentCount) => {
        onSent(sentCount);
      });
    } catch (err) {
      console.error(err);
      setError('Failed to send emails. Please try again.');
      setSending(false);
    }
  }

  // Step 1: Fixture details
  if (step === 1) {
    const valid = date && time && venue && yobs.length && level && gender;
    return (
      <Modal onClose={onClose} title="New Fixture Request">
        <div className="space-y-4">
          <div className="space-y-3">
            <div>
              <Label required>Date</Label>
              <DateSelect value={date} onChange={setDate} />
            </div>
            <div>
              <Label required>Kick-off time</Label>
              <TimeSelect value={time} onChange={setTime} />
            </div>
          </div>
          <div>
            <Label required>Venue</Label>
            <Input value={venue} onChange={(e) => setVenue(e.target.value)} placeholder="Stadium / pitch name and address" />
          </div>
          <div>
            <Label required>Year(s) of birth</Label>
            <YobInput values={yobs} onChange={setYobs} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label required>Level</Label>
              <Sel value={level} onChange={(e) => setLevel(e.target.value)}>
                <option value="">Select level…</option>
                {LEVELS.map((l) => <option key={l}>{l}</option>)}
              </Sel>
            </div>
            <div>
              <Label required>Gender</Label>
              <Sel value={gender} onChange={(e) => setGender(e.target.value)}>
                <option value="">Select…</option>
                {GENDERS.map((g) => <option key={g}>{g}</option>)}
              </Sel>
            </div>
          </div>
          <div>
            <Label required>Email language</Label>
            <div className="flex gap-3">
              {[['en', 'English'], ['es', 'Spanish']].map(([code, label]) => (
                <button
                  key={code} type="button" onClick={() => setLang(code)}
                  className={`flex-1 py-2 rounded-[8px] border-2 text-[13px] font-semibold transition ${
                    lang === code ? 'border-[#1A4A8A] bg-[#EFF6FF] text-[#1A4A8A]' : 'border-[#E5E7EB] text-[#6B7280] hover:border-[#9CA3AF]'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <button
              onClick={() => valid && setStep(2)} disabled={!valid}
              className="px-5 py-2.5 bg-[#1A4A8A] text-white text-[13px] font-semibold rounded-[8px] hover:bg-[#0D2240] transition disabled:opacity-40"
            >
              Next: Select teams →
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  // Step 2: Select teams
  if (step === 2) {
    return (
      <Modal onClose={onClose} title="Select Teams to Contact" wide>
        <div className="space-y-4">
          {/* Filters */}
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
            <Sel value={filterProvince} onChange={(e) => { setFilterProvince(e.target.value); setFilterMuni(''); }}>
              <option value="">All provinces</option>
              {PROVINCES.map((p) => <option key={p}>{p}</option>)}
            </Sel>
            <Sel value={filterMuni} onChange={(e) => setFilterMuni(e.target.value)}>
              <option value="">All towns</option>
              {townsFor(teams, filterProvince).map((m) => <option key={m}>{m}</option>)}
            </Sel>
            <Sel value={filterYob} onChange={(e) => setFilterYob(e.target.value)}>
              <option value="">Any year</option>
              {Array.from({ length: 31 }, (_, i) => String(2020 - i)).map((y) => <option key={y}>{y}</option>)}
            </Sel>
            <Sel value={filterLevel} onChange={(e) => setFilterLevel(e.target.value)}>
              <option value="">Any level</option>
              {LEVELS.map((l) => <option key={l}>{l}</option>)}
            </Sel>
            <Sel value={filterGender} onChange={(e) => setFilterGender(e.target.value)}>
              <option value="">Any gender</option>
              {GENDERS.map((g) => <option key={g}>{g}</option>)}
            </Sel>
            <Sel value={filterSource} onChange={(e) => setFilterSource(e.target.value)}>
              <option value="">Any source</option>
              {SOURCE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </Sel>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-[13px] text-[#6B7280]">{filteredTeams.length} team(s) match filters — {selectedIds.length} selected</p>
            <div className="flex gap-3">
              <button onClick={selectAll} className="text-[12px] text-[#1A4A8A] hover:underline font-medium">Select all</button>
              <button onClick={clearAll}  className="text-[12px] text-[#6B7280] hover:underline font-medium">Clear</button>
            </div>
          </div>

          <div className="max-h-72 overflow-y-auto border border-[#E5E7EB] rounded-[10px] divide-y divide-[#F3F4F6]">
            {filteredTeams.length === 0 && (
              <p className="text-[13px] text-[#9CA3AF] p-4 text-center">No teams match the current filters.</p>
            )}
            {filteredTeams.map((team) => (
              <label key={team.id} className="flex items-start gap-3 p-3 hover:bg-[#F9FAFB] cursor-pointer">
                <input
                  type="checkbox" checked={selectedIds.includes(team.id)}
                  onChange={() => toggleTeam(team.id)}
                  className="mt-0.5 rounded"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold text-[#111827] flex items-center gap-1.5">
                    {team.clubName}<SourceBadge team={team} />
                  </p>
                  <p className="text-[12px] text-[#6B7280]">{team.municipality} ({teamProvince(team)}) · {team.coachName}</p>
                  <p className="text-[11px] text-[#9CA3AF]">
                    {(team.squads || []).slice(0, 4).map((sq) => `${(sq.yearsOfBirth || []).join('/')} · ${sq.gender} · ${sq.level}`).join(' | ')}
                    {(team.squads?.length || 0) > 4 ? ` | +${team.squads.length - 4} more` : ''}
                  </p>
                </div>
              </label>
            ))}
          </div>

          <div className="flex justify-between pt-2">
            <button onClick={() => setStep(1)} className="px-4 py-2 text-[13px] text-[#6B7280] hover:text-[#374151] font-medium">
              ← Back
            </button>
            <button
              onClick={() => selectedIds.length && setStep(3)} disabled={!selectedIds.length}
              className="px-5 py-2.5 bg-[#1A4A8A] text-white text-[13px] font-semibold rounded-[8px] hover:bg-[#0D2240] transition disabled:opacity-40"
            >
              Next: Review & send →
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  // Step 3: Confirm
  const selectedTeams = teams.filter((t) => selectedIds.includes(t.id));
  return (
    <Modal onClose={onClose} title="Review & Send" wide>
      <div className="space-y-4">
        <div className="bg-[#F9FAFB] rounded-[10px] p-4 text-[13px] space-y-1.5">
          <p><span className="text-[#9CA3AF]">Date:</span> <span className="font-semibold">{date}</span></p>
          <p><span className="text-[#9CA3AF]">Time:</span> <span className="font-semibold">{time}</span></p>
          <p><span className="text-[#9CA3AF]">Venue:</span> <span className="font-semibold">{venue}</span></p>
          <p><span className="text-[#9CA3AF]">Year(s) of birth:</span> <span className="font-semibold">{yobs.join(', ')}</span></p>
          <p><span className="text-[#9CA3AF]">Level:</span> <span className="font-semibold">{level}</span></p>
          <p><span className="text-[#9CA3AF]">Gender:</span> <span className="font-semibold">{gender}</span></p>
          <p><span className="text-[#9CA3AF]">Language:</span> <span className="font-semibold">{lang === 'es' ? 'Spanish' : 'English'}</span></p>
        </div>
        <p className="text-[13px] text-[#374151]">
          Emails will be sent to <strong>{selectedIds.length}</strong> team(s):
        </p>
        <div className="max-h-40 overflow-y-auto border border-[#E5E7EB] rounded-[10px] divide-y divide-[#F3F4F6]">
          {selectedTeams.map((t) => (
            <div key={t.id} className="px-3 py-2">
              <p className="text-[13px] font-semibold text-[#111827]">{t.clubName}</p>
              <p className="text-[12px] text-[#6B7280]">{t.coachEmail}</p>
            </div>
          ))}
        </div>
        <div className="bg-[#EFF6FF] border border-[#BFDBFE] rounded-[8px] p-3 text-[12px] text-[#1A4A8A]">
          Each email includes: fixture details, the note that <strong>pitch &amp; referee are paid for</strong>, and three response links (Yes / No / Suggest alternative time).
        </div>
        {error && <div className="bg-red-50 border border-red-200 rounded-[8px] p-3 text-[13px] text-red-700">{error}</div>}
        <div className="flex justify-between pt-2">
          <button onClick={() => setStep(2)} className="px-4 py-2 text-[13px] text-[#6B7280] hover:text-[#374151] font-medium">
            ← Back
          </button>
          <button
            onClick={handleSend} disabled={sending}
            className="px-5 py-2.5 bg-[#065F46] text-white text-[13px] font-semibold rounded-[8px] hover:bg-[#047857] transition disabled:opacity-60"
          >
            {sending ? 'Sending…' : `Send ${selectedIds.length} email(s)`}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Fixture response board ───────────────────────────────────────────────────

function FixtureBoard({ fixture, onClose, onToast }) {
  const [responses, setResponses] = useState([]);
  const [resending, setResending] = useState(false);
  const [resendError, setResendError] = useState('');

  useEffect(() => {
    const q = query(collection(db, 'fixtureRequests', fixture.id, 'responses'));
    return onSnapshot(q, (snap) => {
      setResponses(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, [fixture.id]);

  const yes     = responses.filter((r) => r.status === 'yes');
  const no      = responses.filter((r) => r.status === 'no');
  const suggest = responses.filter((r) => r.status === 'suggest');
  const pending = responses.filter((r) => r.status === 'pending');

  function fmt(dateStr) {
    if (!dateStr) return '—';
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  }

  async function handleResend() {
    if (!pending.length) return;
    setResending(true);
    setResendError('');
    try {
      const fixtureData = { date: fixture.date, time: fixture.time, venue: fixture.venue, yearsOfBirth: fixture.yearsOfBirth, level: fixture.level, gender: fixture.gender };
      const triggerRef = await addDoc(collection(db, 'fixtureEmailRequests'), {
        fixtureId: fixture.id,
        teamIds: pending.map((r) => r.teamId),
        fixture: fixtureData,
        language: fixture.language || 'en',
        requestedAt: serverTimestamp(),
        status: 'pending',
      });
      await new Promise((resolve, reject) => {
        const unsub = onSnapshot(doc(db, 'fixtureEmailRequests', triggerRef.id), (snap) => {
          const d = snap.data();
          if (!d) return;
          if (d.status === 'sent')   { unsub(); resolve(d.sent || 0); }
          if (d.status === 'failed') { unsub(); reject(new Error(d.error || 'Send failed')); }
        });
        setTimeout(() => { unsub(); reject(new Error('Timed out')); }, 30000);
      }).then((sentCount) => {
        onToast?.(`${sentCount} reminder(s) sent!`);
        onClose();
      });
    } catch (err) {
      console.error(err);
      setResendError('Failed to resend. Please try again.');
      setResending(false);
    }
  }

  return (
    <Modal onClose={onClose} title="Fixture Response Board" wide>
      <div className="space-y-4">
        <div className="bg-[#F9FAFB] rounded-[10px] p-4 text-[13px] space-y-1">
          <p><span className="text-[#9CA3AF]">Date:</span> <span className="font-semibold">{fixture.date}</span> at <span className="font-semibold">{fixture.time}</span></p>
          <p><span className="text-[#9CA3AF]">Venue:</span> <span className="font-semibold">{fixture.venue}</span></p>
          <p><span className="text-[#9CA3AF]">Year(s):</span> <span className="font-semibold">{(fixture.yearsOfBirth || []).join(', ')}</span> · <span className="font-semibold">{fixture.level}</span> · <span className="font-semibold">{fixture.gender}</span></p>
        </div>

        {/* Summary pills */}
        <div className="flex gap-3 flex-wrap">
          <span className="px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-[12px] font-bold">{yes.length} Confirmed</span>
          <span className="px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-[12px] font-bold">{no.length} Declined</span>
          <span className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-[12px] font-bold">{suggest.length} Suggested alternative</span>
          <span className="px-3 py-1.5 bg-gray-100 text-gray-500 rounded-full text-[12px] font-bold">{pending.length} Awaiting response</span>
        </div>

        {/* Resend to pending */}
        {pending.length > 0 && (
          <div className="flex items-center justify-between bg-[#F9FAFB] border border-[#E5E7EB] rounded-[8px] px-3 py-2">
            <p className="text-[12px] text-[#6B7280]">{pending.length} team(s) haven't responded yet</p>
            <button
              onClick={handleResend} disabled={resending}
              className="px-3 py-1.5 bg-[#1A4A8A] text-white text-[12px] font-semibold rounded-[8px] hover:bg-[#0D2240] transition disabled:opacity-60"
            >
              {resending ? 'Sending…' : `Resend to ${pending.length}`}
            </button>
          </div>
        )}
        {resendError && <div className="bg-red-50 border border-red-200 rounded-[8px] p-3 text-[12px] text-red-700">{resendError}</div>}

        <div className="border border-[#E5E7EB] rounded-[10px] divide-y divide-[#F3F4F6] max-h-96 overflow-y-auto">
          {responses.length === 0 && (
            <p className="text-[13px] text-[#9CA3AF] p-4 text-center">No responses yet.</p>
          )}
          {responses.map((r) => (
            <div key={r.id} className="flex items-start justify-between gap-3 px-4 py-3">
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold text-[#111827]">{r.clubName}</p>
                <p className="text-[12px] text-[#6B7280]">{r.coachName} · {r.coachEmail}</p>
                {r.status === 'suggest' && r.suggestedDate && (
                  <p className="text-[12px] text-[#1A4A8A] font-medium mt-0.5">
                    Suggested: {fmt(r.suggestedDate)} at {r.suggestedTime}
                  </p>
                )}
              </div>
              <StatusBadge status={r.status} />
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

// Roles allowed to change these databases. The page is also mounted inside the
// tour-leader shell, where the databases are reference material only — a tour
// leader must never be able to delete or amend a club record, or reach the
// sales pipelines at all.
const MANAGE_ROLES = ['staff', 'admin', 'staff_es', 'staff_pad'];

export default function TeamDatabasePage() {
  const { currentUser, userRoles, isAdmin } = useAuth();
  const navigate = useNavigate();
  const basePath = useBasePath();
  const { portals } = useMarket();
  const canManage = isAdmin || (userRoles || []).some((r) => MANAGE_ROLES.includes(r));
  const [activeDb, setActiveDb] = useState(null); // null = nothing picked yet, so the portal's default stands
  const [tab, setTab]           = useState('teams'); // 'teams' | 'fixtures' | 'sales'

  // Which databases (and, within the Spanish database, which tabs) show depends
  // on the portal: UK → Spain uses the Spanish teams + fixtures and the English
  // club leads; Spain → UK uses the Spanish clubs' sales/leads view; Padelante
  // uses its own leads. The combined "All Tours" view shows everything.
  const showUK  = portals.includes('uk_to_spain');
  const showES  = portals.includes('spain_to_uk');
  const showPad = portals.includes('padelante');
  const dbOptions = [
    ...((showUK || showES)      ? [{ key: 'spanish',   label: 'Spanish Clubs & Schools' }] : []),
    ...((showUK && canManage)   ? [{ key: 'english',   label: 'British Clubs & Schools' }] : []),
    ...((showPad && canManage)  ? [{ key: 'padelante', label: 'Padelante Leads' }] : []),
  ];
  const dbKeys = dbOptions.map((d) => d.key);
  // Each portal opens on the database its staff sell from: UK → Spain sells
  // Spain tours to British clubs, Spain → UK sells UK tours to Spanish ones,
  // Padelante works its own leads. The picker still offers everything the user
  // can reach, and a manual choice sticks for the rest of the visit. Anyone
  // without sales access (a tour leader) has no `english` option, so they fall
  // through to the Spanish teams/fixtures database as before.
  const preferredDb = (showUK && 'english') || (showES && 'spanish') || (showPad && 'padelante') || null;
  const effectiveDb = dbKeys.includes(activeDb) ? activeDb
    : (dbKeys.includes(preferredDb) ? preferredDb : (dbKeys[0] || 'spanish'));
  // Anyone who can see the Spanish database (UK → Spain or Spain → UK) sees all
  // of its tabs — Teams, Fixture Requests and Sales/leads.
  const spanishTabs = (showUK || showES)
    ? [['teams', 'Teams'], ['fixtures', 'Fixture Requests'], ...(canManage ? [['sales', 'Sales']] : [])]
    : [];
  const spanishTabKeys = spanishTabs.map((tt) => tt[0]);
  const effectiveTab = spanishTabKeys.includes(tab) ? tab : (spanishTabKeys[0] || 'teams');
  // British Clubs & Schools has its own Leads/CRM sub-tabs, using the same
  // `tab` state as the Spanish sub-tabs above — only one of the two bars ever
  // renders at once, since they're gated by different `effectiveDb` values.
  const englishTabs = (showUK && canManage) ? [['leads', 'Leads'], ['crm', 'Sales']] : [];
  const englishTabKeys = englishTabs.map((tt) => tt[0]);
  const effectiveEnglishTab = englishTabKeys.includes(tab) ? tab : (englishTabKeys[0] || 'leads');
  const [copyingLead, setCopyingLead]         = useState(null); // lead being copied into the CRM
  const [teams, setTeams]       = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [staff, setStaff]       = useState([]);
  const [loading, setLoading]   = useState(true);

  // Teams-tab (fixtures) filters
  const [filterRegion, setFilterRegion] = useState('');
  const [filterProvince, setFilterProvince] = useState('');
  const [filterMuni, setFilterMuni]     = useState('');
  const [filterTeamType, setFilterTeamType] = useState('');
  const [filterYob, setFilterYob]       = useState('');
  const [filterLevel, setFilterLevel]   = useState('');
  const [filterGender, setFilterGender] = useState('');
  // The scraped import outnumbers the registered clubs 260 to 1, so the Teams
  // tab starts on the clubs staff actually know about.
  const [includeScraped, setIncludeScraped] = useState(false);
  const [filterContact, setFilterContact] = useState('');

  // Sales-tab filters — shared with the British database so both read the same.
  const salesFilters = useSalesFilters(currentUser?.uid);

  // Modals
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [showNewFixture, setShowNewFixture] = useState(false);
  const [selectedFixture, setSelectedFixture] = useState(null);
  const [toast, setToast]                   = useState('');

  useEffect(() => {
    const unsub1 = onSnapshot(collection(db, 'teamDatabase'), (snap) => {
      setTeams(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    const unsub2 = onSnapshot(
      query(collection(db, 'fixtureRequests'), orderBy('createdAt', 'desc')),
      (snap) => setFixtures(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    getDocs(query(collection(db, 'users'), limit(500))).then((snap) => {
      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((u) => getUserRoles(u).some((r) => r === 'staff' || r === 'admin'))
        .sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || ''));
      setStaff(list);
    }).catch(() => {});
    return () => { unsub1(); unsub2(); };
  }, []);

  const selectedTeam = teams.find((t) => t.id === selectedTeamId) || null;

  const filteredTeams = teams.filter((team) => {
    if (filterRegion && teamRegion(team) !== filterRegion) return false;
    if (filterProvince && teamProvince(team) !== filterProvince) return false;
    if (filterMuni && team.municipality !== filterMuni) return false;
    if (filterTeamType && team.teamType !== filterTeamType) return false;
    if (!includeScraped && teamSource(team) === 'scraped') return false;
    if (filterContact && hasContactDetails(team) !== (filterContact === 'yes')) return false;
    if (!team.squads?.length && (filterYob || filterLevel || filterGender)) return false;
    if (filterYob || filterLevel || filterGender) {
      return (team.squads || []).some((sq) => {
        if (filterYob    && !sq.yearsOfBirth?.includes(filterYob)) return false;
        if (filterLevel  && sq.level !== filterLevel)               return false;
        if (filterGender && sq.gender !== filterGender)             return false;
        return true;
      });
    }
    return true;
  });

  const salesTeams = teams.filter((team) => {
    const noteText = (Array.isArray(team.salesNoteLog) ? team.salesNoteLog.map((n) => n.text).join(' ') : '') + ' ' + (team.salesNotes || '');
    const contactText = (team.contacts || [])
      .map((c) => [c.name, (c.emails || []).join(' '), (c.phones || []).join(' ')].join(' '))
      .join(' ');
    return salesFilters.matches({
      status: team.salesStatus || '',
      assignedTo: team.salesAssignedTo,
      origin: recordOrigin(team),
      entityType: team.teamType || '',
      hasContact: hasContactDetails(team),
      marketingConsent: team.marketingConsent,
      haystack: [team.clubName, team.municipality, team.coachName, team.coachEmail, contactText, noteText].filter(Boolean).join(' '),
    });
  });

  const salesHiddenScraped = salesFilters.includeScraped
    ? 0
    : teams.filter((t) => recordOrigin(t) === 'scraped').length;

  function changeFilterProvince(p) {
    setFilterProvince(p);
    setFilterMuni(''); // town options depend on the province
  }

  // Narrowing the region can strand a province from the other one selected,
  // which would silently match nothing — so clear anything below it.
  function changeFilterRegion(r) {
    setFilterRegion(r);
    if (r && filterProvince && !provincesFor(r).includes(filterProvince)) {
      setFilterProvince('');
      setFilterMuni('');
    }
  }

  function copyInvite(lang) {
    const url = `${PORTAL_URL}/teamdatabase?lang=${lang}`;
    const msg = INVITE_MSG[lang](url);
    navigator.clipboard.writeText(msg).then(() => {
      setToast(lang === 'es' ? 'Spanish message copied!' : 'English message copied!');
      setTimeout(() => setToast(''), 3000);
    });
  }

  function handleFixtureSent(count) {
    setShowNewFixture(false);
    setToast(`${count} email(s) sent successfully!`);
    setTimeout(() => setToast(''), 4000);
    setTab('fixtures');
  }

  function patchTeam(teamId, data) {
    return updateDoc(doc(db, 'teamDatabase', teamId), data);
  }

  async function handleSalesMarkContacted(team) {
    const existing = normalizeLog(team.salesNoteLog, team.salesNotes);
    const s = (team.salesStatus === 'New' || !team.salesStatus) ? 'Contacted' : team.salesStatus;
    await patchTeam(team.id, {
      salesLastContactedAt: serverTimestamp(),
      salesStatus: s,
      salesNoteLog: [makeNote('Marked as contacted', currentUser, true), ...existing],
      salesNotes: '',
    });
    setToast('Marked as contacted.');
    setTimeout(() => setToast(''), 3000);
  }

  async function handleDeleteTeam(teamId) {
    if (!window.confirm('Delete this team? This cannot be undone.')) return;
    await deleteDoc(doc(db, 'teamDatabase', teamId));
    if (selectedTeamId === teamId) setSelectedTeamId(null);
    setToast('Team deleted.');
    setTimeout(() => setToast(''), 3000);
  }

  async function handleDeleteFixture(fixtureId) {
    if (!window.confirm('Delete this fixture request and all responses? This cannot be undone.')) return;
    const responsesSnap = await getDocs(collection(db, 'fixtureRequests', fixtureId, 'responses'));
    for (const d of responsesSnap.docs) await deleteDoc(d.ref);
    await deleteDoc(doc(db, 'fixtureRequests', fixtureId));
    if (selectedFixture?.id === fixtureId) setSelectedFixture(null);
    setToast('Fixture request deleted.');
    setTimeout(() => setToast(''), 3000);
  }

  // Copies a lead from englishClubLeads straight into a real `requests` doc —
  // the CRM record IS the tour, from day one, so staff can use Quote/Letters/
  // Messages while working the pipeline (those only need customerEmail, not a
  // portal account — that gets attached later, in "Convert to tour"). The
  // lead itself stays put, it just gets tagged and linked.
  async function handleCopyToCrm(lead, tourFields) {
    const { initialNote, numberOfSquads, ...rest } = tourFields;
    const ref = await addDoc(collection(db, 'requests'), {
      customerId: null,
      customerEmail: lead.email || '',
      leaderCostBasis: 'days', // Tour/Junior Lead costed by calendar days present, not nights stayed
      internalRef: lead.clubSchool || '',
      contactName: lead.contactName || '',
      phone: lead.phone || '',
      notes: '',
      ...rest, // sport, destination, departureDate, returnDate, groupType, participants, selectedAddons, largeCaseQuantity
      // Male/female/supporter/coach breakdown isn't captured at this quick
      // copy-to-CRM step — just the total headcount. Staff fill in the rest
      // later via the tour's own "Edit group info" once it's in the CRM.
      participantsMale: null, participantsFemale: null,
      supportersMale: 0, supportersFemale: 0,
      customerStaffMale: 0, customerStaffFemale: 0, customerStaff: 0,
      numberOfSquads: numberOfSquads || null,
      status: 'submitted', category: 'sales_admin', archived: false, stageProgress: {},
      market: 'uk_to_spain', // brand comes from tourFields (rest) — staff pick MST or Your Tour per lead
      crmOrigin: true, crmFlow: 'Cold', crmStage: 'Tour request',
      crmNoteLog: initialNote ? [makeNote(initialNote, currentUser)] : [],
      crmAssignedTo: lead.assignedTo || '', crmAssignedToName: lead.assignedToName || '',
      sourceLeadId: lead.id,
      createdAt: serverTimestamp(), createdByStaff: currentUser?.email || '',
    });
    await updateDoc(doc(db, 'englishClubLeads', lead.id), { inCrm: true, crmRecordId: ref.id });
    setCopyingLead(null);
    navigate(`${basePath}/requests/${ref.id}`);
  }

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(''), 4000);
  }

  // Centre of Spain as default map centre (zoom out far enough to pan to the
  // Canary Islands)
  // Spain and the Canaries are 1,800km apart, so a single centre suits neither;
  // follow whichever region is selected.
  const [mapCentre, mapZoom] = filterRegion === 'Canary Islands' ? [[28.4, -15.9], 7]
    : filterRegion === 'Mainland Spain' ? [[40.2, -3.7], 6]
    : [[34.5, -9.5], 5];

  return (
    <div className="p-6 max-w-[1200px] mx-auto">

      {/* Toast */}
      {toast && (
        <div className="fixed top-5 right-5 z-[99999] bg-[#065F46] text-white px-4 py-3 rounded-[10px] shadow-lg text-[13px] font-semibold">
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-[22px] font-bold text-[#111827]">Databases</h1>
          <div className="mt-2">
            <select
              value={effectiveDb}
              onChange={(e) => setActiveDb(e.target.value)}
              disabled={dbOptions.length <= 1}
              className="border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] font-semibold text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A] bg-white disabled:opacity-100 disabled:cursor-default"
            >
              {dbOptions.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
          </div>
        </div>
        {effectiveDb === 'spanish' && (
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => copyInvite('en')}
              className="px-3 py-2 bg-white border border-[#E5E7EB] text-[12px] font-semibold text-[#374151] rounded-[8px] hover:bg-[#F9FAFB] transition flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3" /></svg>
              Copy invite (EN)
            </button>
            <button onClick={() => copyInvite('es')}
              className="px-3 py-2 bg-white border border-[#E5E7EB] text-[12px] font-semibold text-[#374151] rounded-[8px] hover:bg-[#F9FAFB] transition flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3" /></svg>
              Copy invite (ES)
            </button>
            <button onClick={() => setShowNewFixture(true)}
              className="px-4 py-2 bg-[#1A4A8A] text-white text-[13px] font-semibold rounded-[8px] hover:bg-[#0D2240] transition flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
              New fixture request
            </button>
          </div>
        )}
      </div>

      {/* British clubs & schools (visiting Spain) — England, Scotland, Wales
          and Ireland all share this one database. */}
      {effectiveDb === 'english' && (
        <>
          {englishTabs.length > 1 && (
            <div className="flex border-b border-[#E5E7EB] mb-6">
              {englishTabs.map(([key, label]) => (
                <button
                  key={key} onClick={() => setTab(key)}
                  className={`px-4 py-2.5 text-[13px] font-semibold border-b-2 -mb-px transition ${
                    effectiveEnglishTab === key ? 'border-[#1A4A8A] text-[#1A4A8A]' : 'border-transparent text-[#6B7280] hover:text-[#374151]'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {effectiveEnglishTab === 'leads' && (
            <LeadDatabase
              collectionName="englishClubLeads"
              subtitle="Sell Spain tours to British clubs & schools"
              canManage={canManage}
              enableCrm
              onCopyToCrm={(lead) => setCopyingLead(lead)}
              onViewInCrm={(lead) => lead.crmRecordId && navigate(`${basePath}/requests/${lead.crmRecordId}`)}
            />
          )}

          {effectiveEnglishTab === 'crm' && (
            <CrmDatabase canManage={canManage} basePath={basePath} />
          )}

          {copyingLead && (
            <CopyToCrmModal
              lead={copyingLead}
              onClose={() => setCopyingLead(null)}
              onSave={(tourFields) => handleCopyToCrm(copyingLead, tourFields)}
            />
          )}
        </>
      )}

      {/* Padelante leads (Instagram / organic) */}
      {effectiveDb === 'padelante' && (
        <LeadDatabase
          collectionName="padelanteLeads"
          subtitle="Instagram & organic leads"
          showSource
          padel
          canManage={canManage}
        />
      )}

      {effectiveDb === 'spanish' && (
        <>
          {/* Tabs — shown only if more than one is available for this portal */}
          {spanishTabs.length > 1 && (
          <div className="flex border-b border-[#E5E7EB] mb-6">
            {spanishTabs.map(([key, label]) => (
              <button
                key={key} onClick={() => setTab(key)}
                className={`px-4 py-2.5 text-[13px] font-semibold border-b-2 -mb-px transition ${
                  effectiveTab === key ? 'border-[#1A4A8A] text-[#1A4A8A]' : 'border-transparent text-[#6B7280] hover:text-[#374151]'
                }`}
              >
                {label}
                {key === 'fixtures' && fixtures.length > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 bg-[#E5E7EB] text-[#6B7280] rounded-full text-[11px]">{fixtures.length}</span>
                )}
              </button>
            ))}
          </div>
          )}

          {effectiveTab === 'teams' && (
            <TeamsTab
              teams={filteredTeams} allTeams={teams} loading={loading}
              filterRegion={filterRegion} setFilterRegion={changeFilterRegion}
              filterProvince={filterProvince} setFilterProvince={changeFilterProvince}
              filterMuni={filterMuni} setFilterMuni={setFilterMuni}
              filterTeamType={filterTeamType} setFilterTeamType={setFilterTeamType}
              filterYob={filterYob} setFilterYob={setFilterYob}
              filterLevel={filterLevel} setFilterLevel={setFilterLevel}
              filterGender={filterGender} setFilterGender={setFilterGender}
              includeScraped={includeScraped} setIncludeScraped={setIncludeScraped}
              filterContact={filterContact} setFilterContact={setFilterContact}
              mapCentre={mapCentre} mapZoom={mapZoom}
              canManage={canManage}
              onSelectTeam={(team) => setSelectedTeamId(team.id)}
              onDeleteTeam={handleDeleteTeam}
            />
          )}

          {effectiveTab === 'fixtures' && (
            <FixturesTab
              fixtures={fixtures}
              onSelectFixture={setSelectedFixture}
              onDeleteFixture={handleDeleteFixture}
            />
          )}

          {effectiveTab === 'sales' && (
            <SalesTab
              teams={salesTeams} totalCount={teams.length} loading={loading} staff={staff}
              filters={salesFilters} hiddenScraped={salesHiddenScraped}
              canManage={canManage}
              onSelectTeam={(team) => setSelectedTeamId(team.id)}
              onMarkContacted={handleSalesMarkContacted}
              onDeleteTeam={handleDeleteTeam}
            />
          )}
        </>
      )}

      {/* Modals */}
      {selectedTeam && (
        <TeamDetailModal
          team={selectedTeam}
          staff={staff}
          currentUser={currentUser}
          canManage={canManage}
          onClose={() => setSelectedTeamId(null)}
          onPatch={(data) => patchTeam(selectedTeam.id, data)}
          onDelete={() => handleDeleteTeam(selectedTeam.id)}
        />
      )}
      {showNewFixture && (
        <NewFixtureModal
          teams={teams}
          onClose={() => setShowNewFixture(false)}
          onSent={handleFixtureSent}
        />
      )}
      {selectedFixture && (
        <FixtureBoard fixture={selectedFixture} onClose={() => setSelectedFixture(null)} onToast={showToast} />
      )}
    </div>
  );
}

// ─── Keeps Leaflet in sync when its container resizes (e.g. the staff
// sidebar collapsing/expanding) — Leaflet caches its size and won't notice
// on its own, which is what makes the map look like it overlaps the sidebar.

// MapContainer reads center/zoom on mount only, so switching region would
// otherwise leave the map sat over the mainland with no markers in view.
function MapRecentre({ centre, zoom }) {
  const map = useMap();
  useEffect(() => {
    map.setView(centre, zoom);
  }, [map, centre[0], centre[1], zoom]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function MapResizeHandler() {
  const map = useMap();
  useEffect(() => {
    const container = map.getContainer();
    const observer = new ResizeObserver(() => map.invalidateSize());
    observer.observe(container);
    return () => observer.disconnect();
  }, [map]);
  return null;
}

// ─── Teams tab ────────────────────────────────────────────────────────────────

function TeamsTab({ teams, allTeams, loading, filterRegion, setFilterRegion, filterProvince, setFilterProvince, filterMuni, setFilterMuni, filterTeamType, setFilterTeamType, filterYob, setFilterYob, filterLevel, setFilterLevel, filterGender, setFilterGender, includeScraped, setIncludeScraped, filterContact, setFilterContact, mapCentre, mapZoom, canManage, onSelectTeam, onDeleteTeam }) {
  const teamsWithCoords = allTeams.filter(
    (t) => t.lat && t.lng && (includeScraped || teamSource(t) !== 'scraped')
  );
  const hiddenScraped = includeScraped
    ? 0
    : allTeams.filter((t) => teamSource(t) === 'scraped').length;
  const townOptions = townsFor(allTeams, filterProvince, filterRegion);

  return (
    <div className="space-y-6">
      {/* Map */}
      <div className="bg-white rounded-[12px] border border-[#E5E7EB] overflow-hidden">
        <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
          <p className="text-[13px] font-semibold text-[#374151]">Map — {teamsWithCoords.length} team(s) plotted</p>
          <p className="text-[11px] text-[#9CA3AF]">Click a marker for team details</p>
        </div>
        <div style={{ height: 380 }}>
          <MapContainer center={mapCentre} zoom={mapZoom} style={{ height: '100%', width: '100%' }}>
            <MapResizeHandler />
            <MapRecentre centre={mapCentre} zoom={mapZoom} />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {teamsWithCoords.map((team) => (
              <CircleMarker
                key={team.id}
                center={[team.lat, team.lng]}
                radius={8}
                pathOptions={{ color: '#DC2626', fillColor: '#DC2626', fillOpacity: 0.85, weight: 2 }}
              >
                <Popup>
                  <div style={{ minWidth: 160 }}>
                    <p style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>{team.clubName}</p>
                    <p style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>{team.municipality} · {teamProvince(team)}</p>
                    <p style={{ fontSize: 12, color: '#374151' }}>{team.squads?.length || 0} squad(s)</p>
                    <p style={{ fontSize: 12, color: '#374151' }}>{team.coachName}</p>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <Sel value={filterRegion} onChange={(e) => setFilterRegion(e.target.value)}>
          <option value="">Spain &amp; the Canaries</option>
          {REGIONS.map((r) => <option key={r}>{r}</option>)}
        </Sel>
        <Sel value={filterProvince} onChange={(e) => setFilterProvince(e.target.value)}>
          <option value="">All provinces</option>
          {provincesFor(filterRegion).map((p) => <option key={p}>{p}</option>)}
        </Sel>
        <Sel value={filterMuni} onChange={(e) => setFilterMuni(e.target.value)}>
          <option value="">All towns</option>
          {townOptions.map((m) => <option key={m}>{m}</option>)}
        </Sel>
        <Sel value={filterTeamType} onChange={(e) => setFilterTeamType(e.target.value)}>
          <option value="">School or club</option>
          <option value="club">Club</option>
          <option value="school">School</option>
          <option value="venue">Sports centre</option>
        </Sel>
        <Sel value={filterContact} onChange={(e) => setFilterContact(e.target.value)}>
          <option value="">Any contact details</option>
          {CONTACT_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </Sel>
        <Sel value={filterYob} onChange={(e) => setFilterYob(e.target.value)}>
          <option value="">Any year of birth</option>
          {Array.from({ length: 31 }, (_, i) => String(2020 - i)).map((y) => <option key={y}>{y}</option>)}
        </Sel>
        <Sel value={filterLevel} onChange={(e) => setFilterLevel(e.target.value)}>
          <option value="">Any level</option>
          {LEVELS.map((l) => <option key={l}>{l}</option>)}
        </Sel>
        <Sel value={filterGender} onChange={(e) => setFilterGender(e.target.value)}>
          <option value="">Any gender</option>
          {GENDERS.map((g) => <option key={g}>{g}</option>)}
        </Sel>
        <label className="flex items-center gap-2 text-[13px] text-[#374151] font-medium px-1 cursor-pointer">
          <input type="checkbox" checked={includeScraped}
            onChange={(e) => setIncludeScraped(e.target.checked)} className="rounded" />
          Include scraped teams
        </label>
      </div>

      {/* Team list */}
      {loading ? (
        <p className="text-[#9CA3AF] text-[14px]">Loading teams…</p>
      ) : teams.length === 0 ? (
        <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-8 text-center">
          <p className="text-[15px] font-semibold text-[#374151] mb-1">
            {hiddenScraped > 0 ? 'No teams match your filters' : 'No teams registered yet'}
          </p>
          <p className="text-[13px] text-[#9CA3AF]">
            {hiddenScraped > 0
              ? `${hiddenScraped} scraped team(s) are hidden — tick "Include scraped teams" to search them too.`
              : 'Copy the invite message and share with coaches to get started.'}
          </p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {teams.map((team) => (
            <div
              key={team.id}
              className="bg-white rounded-[12px] border border-[#E5E7EB] p-4 text-left hover:border-[#93C5FD] hover:shadow-sm transition cursor-pointer relative group"
              onClick={() => onSelectTeam(team)}
            >
              {canManage && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onDeleteTeam(team.id); }}
                  className="absolute top-2 right-2 p-1 text-[#D1D5DB] hover:text-[#DC2626] opacity-0 group-hover:opacity-100 transition rounded"
                  title="Delete team"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
              <p className="text-[14px] font-bold text-[#111827] mb-0.5 pr-6">{team.clubName}</p>
              <p className="text-[12px] text-[#6B7280] mb-2">
                {team.municipality} · {teamProvince(team)}
                {team.teamType && <span className="ml-1.5 px-1.5 py-0.5 bg-[#EFF6FF] text-[#1A4A8A] rounded-full text-[10px] font-semibold uppercase">{TEAM_TYPE_LABELS[team.teamType] || team.teamType}</span>}
                {teamSource(team) === 'scraped' && <span className="ml-1.5"><SourceBadge team={team} /></span>}
              </p>
              {/* A registered club has a squad or two; a scraped one can have
                  thirty, so summarise past the first few. */}
              <div className="flex flex-wrap items-center gap-1.5 mb-2">
                {(team.squads || []).slice(0, 6).map((sq, i) => (
                  <span key={i} className="flex items-center gap-1">
                    <LevelBadge level={sq.level} />
                    <span className="text-[11px] text-[#9CA3AF]">{sq.gender}</span>
                  </span>
                ))}
                {(team.squads?.length || 0) > 6 && (
                  <span className="text-[11px] text-[#6B7280] font-semibold">
                    +{team.squads.length - 6} more team(s)
                  </span>
                )}
              </div>
              <div className="flex gap-2 mt-2">
                {(team.squads || []).filter((sq) => sq.homeJerseyColour).flatMap((sq, si) => [
                  <KitModel key={`h-${si}`} uid={`list-h-${team.id}-${si}`} size="sm"
                    jerseyColour={sq.homeJerseyColour} jerseyColour2={sq.homeJerseyColour2}
                    jerseyPattern={sq.homeJerseyPattern} shortsColour={sq.homeShortsColour} socksColour={sq.homeSocksColour}
                  />,
                  !sq.onlyOneKit && <KitModel key={`a-${si}`} uid={`list-a-${team.id}-${si}`} size="sm"
                    jerseyColour={sq.awayJerseyColour} jerseyColour2={sq.awayJerseyColour2}
                    jerseyPattern={sq.awayJerseyPattern} shortsColour={sq.awayShortsColour} socksColour={sq.awaySocksColour}
                  />,
                ]).filter(Boolean)}
              </div>
              <p className="text-[11px] text-[#9CA3AF] mt-2">{team.coachName} · {team.coachEmail}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Sales tab (UK-tour pipeline over the registered Spanish clubs) ───────────

function SalesTab({ teams, totalCount, loading, staff, filters, hiddenScraped, canManage, onSelectTeam, onMarkContacted, onDeleteTeam }) {
  function fmt(ts) {
    if (!ts) return null;
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    if (isNaN(d)) return null;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  return (
    <div className="space-y-5">
      <p className="text-[13px] text-[#6B7280]">
        Sell UK tours to Spanish clubs &amp; schools — {totalCount} club(s){teams.length !== totalCount ? ` · ${teams.length} shown` : ''}
        {hiddenScraped > 0 && <span className="text-[#9CA3AF]"> · {hiddenScraped} scraped hidden</span>}
      </p>

      {/* Filters — the same set the British database shows */}
      <SalesFilterBar f={filters} staff={staff} showConsent />

      {/* List */}
      {loading ? (
        <p className="text-[#9CA3AF] text-[14px]">Loading clubs…</p>
      ) : teams.length === 0 ? (
        <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-8 text-center">
          <p className="text-[15px] font-semibold text-[#374151] mb-1">
            {totalCount === 0 ? 'No clubs registered yet' : 'No clubs match your filters'}
          </p>
          <p className="text-[13px] text-[#9CA3AF]">
            {totalCount === 0
              ? 'Clubs appear here once they register via the invite link.'
              : hiddenScraped > 0
                ? `${hiddenScraped} scraped club(s) are hidden — tick "Include scraped teams" to work them too.`
                : 'Try clearing the search or filters.'}
          </p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {teams.map((team) => {
            const contacted = fmt(team.salesLastContactedAt);
            const latest = normalizeLog(team.salesNoteLog, team.salesNotes)[0];
            const kitSquads = (team.squads || []).filter((sq) => sq.homeJerseyColour).slice(0, 3);
            return (
              <div
                key={team.id}
                onClick={() => onSelectTeam(team)}
                className="bg-white rounded-[12px] border border-[#E5E7EB] p-4 flex flex-col text-left hover:border-[#93C5FD] hover:shadow-sm transition cursor-pointer relative group"
              >
                {canManage && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onDeleteTeam(team.id); }}
                    className="absolute top-2 right-2 p-1 text-[#D1D5DB] hover:text-[#DC2626] opacity-0 group-hover:opacity-100 transition rounded bg-white"
                    title="Delete club"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="text-[14px] font-bold text-[#111827] pr-1 flex-1 min-w-0">{team.clubName}</p>
                  <SourceBadge team={team} />
                  {team.salesStatus ? <SalesBadge status={team.salesStatus} /> : <span className="text-[11px] text-[#9CA3AF] flex-shrink-0">Not started</span>}
                </div>
                <p className="text-[12px] text-[#6B7280]">
                  {team.municipality} · {teamProvince(team)}
                  {team.teamType && (
                    <span className="ml-1.5 px-1.5 py-0.5 bg-[#EFF6FF] text-[#1A4A8A] rounded-full text-[10px] font-semibold uppercase">
                      {TEAM_TYPE_LABELS[team.teamType] || team.teamType}
                    </span>
                  )}
                </p>
                {recordOrigin(team) === 'client' && (
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className="px-1.5 py-0.5 bg-[#ECFDF5] text-[#065F46] rounded-full text-[10px] font-semibold uppercase tracking-wide">Client</span>
                    {team.tourName && <span className="text-[11px] text-[#9CA3AF] truncate">{team.tourName}</span>}
                  </div>
                )}
                {team.coachName && <p className="text-[12px] text-[#6B7280] mt-0.5">{[team.coachName, team.coachEmail].filter(Boolean).join(' · ')}</p>}
                {team.marketingConsent && <p className="text-[11px] font-semibold text-[#065F46] mt-1">✓ Marketing opt-in</p>}

                {/* Kit, when the club gave one — scraped records have no
                    colours and would otherwise draw a row of blank shirts. */}
                {kitSquads.length > 0 && (
                  <div className="flex gap-2 mt-2">
                    {kitSquads.flatMap((sq, si) => [
                      <KitModel key={`h-${si}`} uid={`sales-h-${team.id}-${si}`} size="sm"
                        jerseyColour={sq.homeJerseyColour} jerseyColour2={sq.homeJerseyColour2}
                        jerseyPattern={sq.homeJerseyPattern} shortsColour={sq.homeShortsColour} socksColour={sq.homeSocksColour}
                      />,
                      !sq.onlyOneKit && sq.awayJerseyColour && (
                        <KitModel key={`a-${si}`} uid={`sales-a-${team.id}-${si}`} size="sm"
                          jerseyColour={sq.awayJerseyColour} jerseyColour2={sq.awayJerseyColour2}
                          jerseyPattern={sq.awayJerseyPattern} shortsColour={sq.awayShortsColour} socksColour={sq.awaySocksColour}
                        />
                      ),
                    ]).filter(Boolean)}
                  </div>
                )}

                {latest && <p className="text-[12px] text-[#374151] mt-2 line-clamp-2 whitespace-pre-wrap">{latest.text}</p>}

                <div className="mt-3 pt-3 border-t border-[#F3F4F6] flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[11px] text-[#9CA3AF] truncate">
                      {team.salesAssignedToName ? `Assigned: ${team.salesAssignedToName}` : 'Unassigned'}
                    </p>
                    {contacted && <p className="text-[11px] text-[#9CA3AF]">Contacted: {contacted}</p>}
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); onMarkContacted(team); }}
                    title="Mark contacted today"
                    className="p-1.5 text-[#9CA3AF] hover:text-[#065F46] transition rounded flex-shrink-0"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Fixtures tab ─────────────────────────────────────────────────────────────

function FixturesTab({ fixtures, onSelectFixture, onDeleteFixture }) {
  function fmt(dateStr) {
    if (!dateStr) return '—';
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  }

  if (fixtures.length === 0) {
    return (
      <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-8 text-center">
        <p className="text-[15px] font-semibold text-[#374151] mb-1">No fixture requests yet</p>
        <p className="text-[13px] text-[#9CA3AF]">Click "New fixture request" to get started.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {fixtures.map((fx) => (
        <div
          key={fx.id}
          onClick={() => onSelectFixture(fx)}
          className="w-full bg-white rounded-[12px] border border-[#E5E7EB] p-4 text-left hover:border-[#93C5FD] hover:shadow-sm transition cursor-pointer relative group"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDeleteFixture(fx.id); }}
            className="absolute top-3 right-3 p-1 text-[#D1D5DB] hover:text-[#DC2626] opacity-0 group-hover:opacity-100 transition rounded"
            title="Delete fixture"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
          <div className="flex items-start justify-between gap-3 pr-8">
            <div className="flex-1 min-w-0">
              <p className="text-[14px] font-bold text-[#111827]">{fmt(fx.date)} at {fx.time}</p>
              <p className="text-[13px] text-[#6B7280] mt-0.5">{fx.venue}</p>
              <p className="text-[12px] text-[#9CA3AF] mt-1">
                {(fx.yearsOfBirth || []).join(', ')} · {fx.level} · {fx.gender} · {fx.language === 'es' ? 'Spanish' : 'English'}
              </p>
            </div>
            <span className="text-[12px] text-[#1A4A8A] font-semibold flex-shrink-0 flex items-center gap-1">
              View responses
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}
